<template>
  <div id="portfolio-view">
    <h1 class="portfolio-title text-start pt-10">
      <span class="accent--text">{{ $t('shared.unique') }}. </span>
      <span class="primary--text">{{ $t('shared.rare') }}. </span>
      <span class="secondary--text">{{ $t('shared.handmade') }}.</span>
    </h1>
    <h2 class="pt-2 pb-5 text-start portfolio-subtitle">{{ $t('shared.whatYouStand') }}</h2>
    <div class="pa-12 text-center" v-if="isLoading">
      <v-progress-circular indeterminate class="mx-auto" size="60" width="5" />
    </div>
    <v-row class="h-100 pa-12" justify="center" align="center" v-else-if="!isWalletConnectedToChain">
      <v-btn v-if="isWalletConnected"
        color="secondary"
        x-large
        outlined
        @click="$store.dispatch('web3SwitchChain')"
      >{{ $t('shared.switchToEth') }}</v-btn>
      <v-btn v-else
        color="secondary"
        x-large
        outlined
        @click="$store.dispatch('web3Connect')"
      >{{ $t('shared.connectWallet') }}</v-btn>
    </v-row>
    <div v-else-if="!tokensData || !tokensData.length">
      <div class="pt-12 pb-4 text-body-2 font-weight-medium">{{ $t('token.noTokensInPortfolio' )}}</div>
      <div class="py-4" v-if="totalTokensMinted < totalTokens">
        <v-btn
          large
          outlined
          color="primary"
          :to="{ name: 'minting' }"
        >{{ $t('minting.mintInSale' )}}</v-btn>
      </div>
      <div class="py-4" v-else>
        <v-btn
          large
          outlined
          color="primary"
          :href="marketplaceCollectionUri"
        >{{ $t('token.buyInOpenSea' )}}</v-btn>
      </div>
    </div>
    <v-row justify="center" align-content="stretch" v-else>
      <v-col v-for="tokenData of tokensData"
        :key="'token-col-' + tokenData.tokenId"
        cols="24" sm="12" md="8" lg="6"
        class="pa-2 text-center"
      >
        <qb-token-item-card :token-data="tokenData" />
      </v-col>
    </v-row>
  </div>
</template>

<style lang="scss">
  @import '@/styles/main.scss';

  #portfolio-view {
    margin-bottom: 300px;
    h1.portfolio-title {
      font-size: 42px;
      line-height: 42px;
      @include mdAndUp {
        font-size: 70px;
        line-height: 70px;
      }
    }
    h2.portfolio-subtitle {
      font-size: 20px;
      line-height: 20px;
      @include mdAndUp {
        font-size: 42px;
        line-height: 42px;
      }
    }
  }

</style>

<script>
import QbTokenItemCard from '@/components/nfts/QbTokenItemCard.vue'
import { translateIpfsUri } from '@/filters'
import { totalTokens, marketplaceCollectionUri } from '@/data/constants.js'

export default {
  components: {
    QbTokenItemCard
  },
  data () {
    return {
      isLoading: true,
      totalTokensMinted: null,
      accountTokensBalance: null,
      tokensData: []
    }
  },
  metaInfo () {
    return {
      title: 'The QBits - ' + this.$t('nav.portfolio')
    }
  },
  computed: {
    marketplaceCollectionUri: () => marketplaceCollectionUri,
    totalTokens: () => totalTokens,
    web3LastChangeTimestamp () {
      return this.$store.getters.web3LastChangeTimestamp
    },
    ethProvider () {
      return this.$store.getters.ethProvider
    },
    ethContractInstance () {
      return this.$store.getters.ethContractInstance
    },
    isWalletConnected () {
      return this.$store.getters.web3Connected &&
          !!this.$store.getters.web3Account
    },
    isChainMatched () {
      return this.$store.getters.web3ChainMatch
    },
    isWalletConnectedToChain () {
      return this.isWalletConnected && this.isChainMatched
    },
    web3Account () {
      return this.$store.getters.web3Account
    }
  },
  methods: {
    updateInfoFromChain () {
      if (this.isWalletConnectedToChain) {
        this.ethContractInstance.totalSupply()
          .then(result => {
            this.totalTokensMinted = result.toNumber()
            return this.ethContractInstance.balanceOf(this.web3Account)
          })
          .then(result => { this.accountTokensBalance = result.toNumber() })
          .catch(() => {
            this.accountTokensBalance = null
            this.isLoading = false
            this.$store.dispatch('alertShow', { error: this.$t('error.fetchingContractData') })
          })
      } else {
        this.accountBalanceExact = null
        this.accountTokensBalance = null
        this.isLoading = false
      }
    },
    async updatePortfolioFromChain () {
      if (!this.accountTokensBalance) {
        this.tokensData = []
        this.isLoading = false
        return
      }
      this.isLoading = true
      const tokensDataArray = []
      for (let iTokenInPortfolio = 0; iTokenInPortfolio < this.accountTokensBalance; iTokenInPortfolio++) {
        const tokenData = {}
        try {
          tokenData.tokenId = (await this.ethContractInstance.tokenOfOwnerByIndex(this.web3Account, iTokenInPortfolio)).toNumber()
          const tokenMetadataUri = translateIpfsUri(
            (await this.ethContractInstance.tokenURI(tokenData.tokenId))
              .replace(/\{id\}}/g, tokenData.tokenId.toString())
          )
          const tokenMetadataResponse = await fetch(tokenMetadataUri, {
            method: 'GET',
            headers: {
              Accept: 'application/json'
            }
          })
          if (!tokenMetadataResponse.ok) { throw new Error() }
          const tokenMetadata = await tokenMetadataResponse.json()
          tokenData.title = tokenMetadata.name
          tokenData.imageUri = translateIpfsUri(
            tokenMetadata.image
              .replace(/\{id\}}/g, tokenData.tokenId.toString())
          )
          tokenData.description = tokenMetadata.description
        } catch (error) {
          console.error(error) // eslint-disable-line no-console
          this.$store.dispatch('alertShow', { error: this.$t('error.fetchingTokensData') })
        }
        if (tokenData.tokenId !== undefined) {
          tokensDataArray.push(tokenData)
        }
      }
      this.tokensData = tokensDataArray.sort((a, b) => (a.tokenId - b.tokenId))
      this.isLoading = false
    }
  },
  watch: {
    web3LastChangeTimestamp () {
      this.updateInfoFromChain()
    },
    accountTokensBalance () {
      this.updatePortfolioFromChain()
    }
  },
  mounted () {
    this.updateInfoFromChain()
  }
}
</script>
